import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wand2, Check, ArrowRight, ArrowLeft, Loader2, Sparkles } from "lucide-react";

const STEPS = [
  { n: 1, name: "Business Goal", desc: "Objective, target & budget ceiling" },
  { n: 2, name: "Product", desc: "What are you selling" },
  { n: 3, name: "Audience", desc: "Location & targeting hints" },
  { n: 4, name: "Offer & Language", desc: "Promo angle & copy language" },
  { n: 5, name: "Budget", desc: "Daily spend" },
  { n: 6, name: "Generate & Review", desc: "AI builds the campaign" },
];

const OBJECTIVES = ["WhatsApp Leads", "Website Leads", "Sales", "Booking", "Calls", "Awareness"];

export default function CampaignBuilder() {
  const { lang } = useI18n();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ objective: "WhatsApp Leads", product: "", location: "Malaysia", audience: "", offer: "", budget: 100, language: lang === "bm" ? "Malay" : "English" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/campaign-builder", { ...form, budget: Number(form.budget) });
      setResult(r.data);
      toast.success("AI campaign generated");
    } catch (e) { toast.error("Generation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-float-up" data-testid="builder-panel">
      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin pb-1">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 shrink-0 ${step === s.n ? "text-primary" : step > s.n ? "text-accent" : "text-muted-foreground"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${step === s.n ? "border-primary bg-primary/15" : step > s.n ? "border-accent bg-accent/15" : "border-border"}`}>
                {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
              </div>
              <span className="text-xs font-medium hidden sm:block">{s.name}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-6 h-px bg-border shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 min-h-[320px]">
        <h3 className="font-display font-semibold text-lg mb-1">Step {step}: {STEPS[step - 1].name}</h3>
        <p className="text-sm text-muted-foreground mb-6">{STEPS[step - 1].desc}</p>

        {step === 1 && (
          <div className="space-y-2 max-w-md">
            <Label>Objective</Label>
            <Select value={form.objective} onValueChange={(v) => set("objective", v)}>
              <SelectTrigger data-testid="builder-objective"><SelectValue /></SelectTrigger>
              <SelectContent>{OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-2 max-w-md">
            <Label>Product / Service</Label>
            <Input data-testid="builder-product" placeholder="e.g. Umrah Deluxe Package" value={form.product} onChange={(e) => set("product", e.target.value)} />
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4 max-w-md">
            <div className="space-y-2"><Label>Location</Label><Input data-testid="builder-location" value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
            <div className="space-y-2"><Label>Audience hint</Label><Input data-testid="builder-audience" placeholder="e.g. Muslim families 30-55" value={form.audience} onChange={(e) => set("audience", e.target.value)} /></div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-4 max-w-md">
            <div className="space-y-2"><Label>Offer</Label><Textarea data-testid="builder-offer" placeholder="e.g. Early bird RM500 off, free visa processing" value={form.offer} onChange={(e) => set("offer", e.target.value)} /></div>
            <div className="space-y-2"><Label>Copy Language</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger data-testid="builder-language"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="English">English</SelectItem><SelectItem value="Malay">Malay</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        {step === 5 && (
          <div className="space-y-2 max-w-md">
            <Label>Daily Budget (RM)</Label>
            <Input data-testid="builder-budget" type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
          </div>
        )}
        {step === 6 && (
          <div>
            {!result && !loading && (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground mb-5">Review your inputs, then let AI build the full campaign structure, audience, copy, creative & tracking.</p>
                <div className="grid grid-cols-2 gap-2 text-left text-xs bg-secondary/40 rounded-xl p-4 mb-6 max-w-md mx-auto">
                  <span className="text-muted-foreground">Objective</span><span className="font-medium">{form.objective}</span>
                  <span className="text-muted-foreground">Product</span><span className="font-medium">{form.product || "—"}</span>
                  <span className="text-muted-foreground">Location</span><span className="font-medium">{form.location}</span>
                  <span className="text-muted-foreground">Budget</span><span className="font-medium">RM{form.budget}/day</span>
                  <span className="text-muted-foreground">Language</span><span className="font-medium">{form.language}</span>
                </div>
                <Button data-testid="builder-generate" onClick={generate} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl px-8">
                  <Sparkles className="w-4 h-4 mr-1" /> Generate with AI
                </Button>
              </div>
            )}
            {loading && <div className="flex flex-col items-center py-10 gap-3"><Loader2 className="w-7 h-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">AI is building your campaign…</p></div>}
            {result && (
              <div className="space-y-4 animate-float-up" data-testid="builder-result">
                <Block title="Campaign Structure"><p className="font-medium">{result.campaign_structure?.name}</p><ul className="list-disc list-inside text-sm text-muted-foreground mt-1">{(result.campaign_structure?.adsets || []).map((a, i) => <li key={i}>{a}</li>)}</ul></Block>
                <Block title="Audience Recommendation"><ul className="list-disc list-inside text-sm text-muted-foreground">{(result.audience_recommendation || []).map((a, i) => <li key={i}>{a}</li>)}</ul></Block>
                <Block title="Ad Copy">
                  <p className="text-sm"><b>Primary:</b> {result.ad_copy?.primary_text}</p>
                  <p className="text-sm mt-1"><b>Headline:</b> {result.ad_copy?.headline}</p>
                  <p className="text-sm mt-1"><b>CTA:</b> {result.ad_copy?.cta}</p>
                </Block>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Block title="Budget Recommendation"><p className="text-sm">RM{result.budget_recommendation?.daily}/day — {result.budget_recommendation?.split}</p><p className="text-xs text-muted-foreground mt-1">{result.budget_recommendation?.note}</p></Block>
                  <Block title="Estimated Results"><div className="grid grid-cols-2 gap-1 text-sm">
                    <span className="text-muted-foreground">Leads</span><span>{result.estimated_results?.leads}</span>
                    <span className="text-muted-foreground">Sales</span><span>{result.estimated_results?.sales}</span>
                    <span className="text-muted-foreground">ROAS</span><span className="text-primary font-semibold">{result.estimated_results?.roas}</span>
                  </div></Block>
                </div>
                <Button variant="outline" data-testid="builder-reset" onClick={() => { setResult(null); setStep(1); }} className="rounded-xl">Start New Campaign</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {!result && (
        <div className="flex justify-between">
          <Button variant="outline" data-testid="builder-back" disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="rounded-xl"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {step < 6 && <Button data-testid="builder-next" onClick={() => setStep((s) => s + 1)} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>}
        </div>
      )}
    </div>
  );
}

const Block = ({ title, children }) => (
  <div className="bg-secondary/30 border border-border rounded-xl p-4">
    <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2 flex items-center gap-1"><Wand2 className="w-3 h-3" />{title}</p>
    {children}
  </div>
);
